import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { from, Observable, catchError, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { CsrfData, LoginRequest, LoginSuccessData, SessionInfoDto } from '../dtos/auth';
import {
  CSRF_ENDPOINT,
  LOGIN_ENDPOINT,
  LOGOUT_ENDPOINT,
  SESSION_ENDPOINT,
} from '../constants/endpoints';
import { ApiError, FieldError } from '../dtos/api-error';
import { SessionStore } from '../stores/session.store';
import { ApiResponse } from '../dtos/api-response';
import { Session, SessionMapper } from '../models/session';

/** LoginResult returned to components calling the login method */
export type LoginResult =
  | { success: true; message: string }
  | { success: false; status: number; message: string; fieldErrors?: FieldError[] };

@Injectable({ providedIn: 'root' })
export class AuthService {
  // Injected dependencies
  private readonly http = inject(HttpClient);
  private readonly sessionStore = inject(SessionStore);
  private readonly socialAuthService = inject(SocialAuthService);

  /**
   * Fetches CSRF token and header name. Call before login POST.
   * Uses withCredentials so cookies are sent/received.
   */
  getCsrf(): Observable<CsrfData> {
    return this.http
      .get<ApiResponse<CsrfData>>(CSRF_ENDPOINT, { responseType: 'json', withCredentials: true })
      .pipe(map((resp) => resp.data));
  }

  /**
   * Fetches session details from the backend. Uses the auth cookie (withCredentials).
   * 200 → returns Session from response.data; 401 or error → returns null.
   */
  getSession(): Observable<Session | null> {
    return this.http.get<ApiResponse<SessionInfoDto>>(SESSION_ENDPOINT).pipe(
      map((resp) => SessionMapper.fromDto(resp.data)),
      catchError((_err: HttpErrorResponse) => of(null)),
    );
  }

  /** Shared so multiple subscribers (e.g. app component + guard) trigger only one request. */
  private readonly session$ = this.getSession().pipe(
    tap((session) => this.sessionStore.setSession(session)),
    shareReplay(1),
  );

  /**
   * Loads session and updates the session store. Call on app init or when you need to refresh session state.
   * Uses a shared observable so multiple subscribers trigger only one request.
   */
  loadSession(): Observable<Session | null> {
    return this.session$;
  }

  /**
   * Fetches session once and updates the session store. Use after login so the new cookie is reflected (bypasses loadSession cache).
   */
  refreshSession(): Observable<Session | null> {
    return this.getSession().pipe(tap((session) => this.sessionStore.setSession(session)));
  }

  /**
   * Logs in with a Google ID token. On 200, fetches session, updates the session store,
   * then emits success. The browser will send auth cookies on subsequent requests.
   */
  login(idToken: string): Observable<LoginResult> {
    const body: LoginRequest = { idToken };

    return this.http.post<ApiResponse<LoginSuccessData>>(LOGIN_ENDPOINT, body).pipe(
      switchMap((resp) =>
        this.getSession().pipe(
          tap((session) => this.sessionStore.setSession(session)),
          map(() => ({ success: true as const, message: resp.data.message })),
        ),
      ),
      catchError((err: HttpErrorResponse) => {
        const body = err.error as ApiError;
        return of({
          success: false as const,
          status: err.status ?? 0,
          message: body?.message ?? 'Login failed',
          fieldErrors: body?.fieldErrors ?? undefined,
        });
      }),
    );
  }

  /**
   * Logs the user out by calling the backend logout endpoint. The server clears the auth cookie
   * via Set-Cookie. Requires the XSRF-TOKEN cookie to be present (set by CSRF endpoint or a
   * prior request); the CSRF interceptor sends it in the X-XSRF-TOKEN header.
   * On success (200, 204, or 302), clears client-side session state and signs out of Google so
   * the login page does not immediately re-authenticate via authState.
   */
  logout(): Observable<void> {
    return this.http.post(LOGOUT_ENDPOINT, {}).pipe(
      tap(() => this.sessionStore.setSession(null)),
      switchMap(() =>
        from(this.socialAuthService.signOut(true).catch(() => undefined)),
      ),
      map(() => undefined),
    );
  }
}
